import { Popover as PrimerPopover, ThemeProvider as _PrimerThemeProvider } from "@primer/react";
import React, { FunctionComponent, PropsWithChildren, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import isElementDescendant from "manifest-core/utils/isElementDescendant";
import { ColorVariables } from "../Colors";

const PrimerThemeProvider = _PrimerThemeProvider as FunctionComponent<PropsWithChildren<any>>;

export { PrimerThemeProvider };

const StyledPopoverContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledPrimerPopover = styled(PrimerPopover)`
  > * {
    width: auto;
  }
`;

const StyledPopoverLinks = styled.div`
  a,
  button {
    display: block;
    text-align: left;
    margin-bottom: 16px !important;
    text-decoration: none;
    font-size: 14px;
    line-height: 22px;
    color: ${ColorVariables.coco7};
    font-weight: 500;
    &:hover {
      color: ${ColorVariables.mint};
    }
    &.active {
      color: ${ColorVariables.mint9};
      font-weight: 600;
    }
    &:last-child {
      margin-bottom: 0 !important;
    }
  }
`;

export type PopoverProps = PropsWithChildren<{
  content: JSX.Element;
  popoverContent: JSX.Element;
  style?: React.CSSProperties;
  caret?: string;
}>;

function PopoverLinks(props: PropsWithChildren<any>) {
  return <StyledPopoverLinks>{props.children}</StyledPopoverLinks>;
}

function Popover(props: PopoverProps) {
  const caret = props.caret || "top";
  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  const popoverContainerRef = useRef<HTMLDivElement>(null);

  const handleToggleOpen = () => {
    setOpen(!open);
  };

  const handleWindowClick = (e: any) => {
    if (isElementDescendant(popoverContainerRef.current, e.target)) return;
    if (openRef.current) {
      setOpen(false);
    }
  };

  const handlePopoverContentClicked = (e: any) => {
    if (e.target.tagName === "A" || e.target.tagName === "BUTTON") {
      setOpen(false);
    }
  };

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    window.addEventListener("click", handleWindowClick);
    return () => {
      window.removeEventListener("click", handleWindowClick);
    };
  }, []);

  return (
    <StyledPopoverContainer ref={popoverContainerRef}>
      <div role="button" onClick={handleToggleOpen}>
        {props.content}
      </div>
      <StyledPrimerPopover open={open} caret={caret}>
        <PrimerPopover.Content onClick={handlePopoverContentClicked} style={props.style}>
          {props.popoverContent}
        </PrimerPopover.Content>
      </StyledPrimerPopover>
    </StyledPopoverContainer>
  );
}

Popover.Links = PopoverLinks;

export default Popover;
